import { useMemo, useRef, type CSSProperties } from "react";
import { Vector3 } from "three";
import {
  hotspotTooltipLabel,
  sphericalToDirection,
  useCoarsePointer,
  useTourState,
} from "@heroncendre/player";
import { useEtatNavigation } from "./etatNavigation.js";
import { useProjectionHotspots } from "./useProjectionHotspots.js";
import { COULEURS, POLICE_UI_SPLASH } from "./splashStyles.js";

/**
 * ============================================================================
 *  NomsHotspots.tsx — Où mène chaque point, sans survol
 * ----------------------------------------------------------------------------
 *  Le moteur annonce la destination d'un hotspot dans une infobulle au SURVOL
 *  (`HotspotTooltip`). Au doigt il n'y a pas de survol : le visiteur devrait
 *  toucher chaque disque pour savoir où il mène — et s'y déplacer. Sur
 *  téléphone, on affiche donc en permanence le nom au-dessus de chaque point.
 *
 *  ── La projection ─────────────────────────────────────────────────────────
 *
 *  Voir `useProjectionHotspots` : le registre du moteur n'est pas exposé, on
 *  refait sa projection.
 *
 *  Le libellé est celui de l'infobulle (`hotspotTooltipLabel`) : une seule règle
 *  pour le nom, au survol comme ici.
 *
 *  Écart assumé avec le moteur : à un changement de scène, les étiquettes ne
 *  s'effacent pas en fondu devant le panorama qui part (le moteur tient cette
 *  mémoire dans `HotspotPresence`, qu'on ne peut pas lire) ; elles suivent la
 *  scène courante.
 * ==========================================================================*/

export function NomsHotspots() {
  const mobile = useCoarsePointer();
  if (!mobile) return null;
  return <Etiquettes />;
}

function Etiquettes() {
  const tour = useTourState((s) => s.tour);
  const language = useTourState((s) => s.context.language);
  const sceneId = useTourState((s) => s.currentSceneId);
  const navOuverte = useEtatNavigation((e) => e.ouvert);
  const cadre = useRef<HTMLDivElement>(null);
  const elements = useRef(new Map<string, HTMLDivElement>());

  const points = useMemo(() => {
    const scene = tour.scenes.find((s) => s.id === sceneId);
    return (scene?.hotspots ?? []).flatMap((hotspot) => {
      // Un point vers une scène que le tour ne connaît pas : pas de nom à donner.
      if (hotspot.type === "nav" && !tour.scenes.some((s) => s.id === hotspot.target)) return [];
      const libelle = hotspotTooltipLabel(tour, hotspot, language);
      return libelle ? [{ id: hotspot.id, libelle, direction: sphericalToDirection(hotspot.position, new Vector3()) }] : [];
    });
  }, [tour, sceneId, language]);

  useProjectionHotspots(cadre, points, !navOuverte, (id, ecran) => {
    const element = elements.current.get(id);
    if (!element) return;
    if (!ecran) {
      element.style.visibility = "hidden";
      return;
    }
    element.style.visibility = "visible";
    element.style.transform = `translate3d(${ecran.x}px, ${ecran.y}px, 0) translate(-50%, calc(-100% - ${DECALAGE}px))`;
  });

  if (navOuverte) return null;

  return (
    <div ref={cadre} aria-hidden style={calque}>
      {points.map((point) => (
        <div
          key={point.id}
          ref={(element) => {
            if (element) elements.current.set(point.id, element);
            else elements.current.delete(point.id);
          }}
          style={etiquette}
        >
          {point.libelle}
        </div>
      ))}
    </div>
  );
}

/** Écart entre le centre du point et le bas de l'étiquette, en pixels d'écran. */
const DECALAGE = 26;

const calque: CSSProperties = {
  position: "absolute",
  inset: 0,
  overflow: "hidden",
  pointerEvents: "none",
};

const etiquette: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  // Caché tant que la première position n'est pas calculée : sans cela, l'étiquette
  // apparaîtrait une image dans le coin haut gauche.
  visibility: "hidden",
  maxWidth: "70vw",
  padding: "4px 9px",
  background: "rgba(16,26,23,.82)",
  color: COULEURS.creme,
  border: "1px solid rgba(246,238,227,.22)",
  borderRadius: 2,
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.25,
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  willChange: "transform",
};
